import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ArrowLeft, LineChart, Briefcase, User, Newspaper, MessageCircle, Calendar } from 'lucide-react';

function CalendarPage({ user, setUser }) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewsMenu, setShowNewsMenu] = useState(false);

  // Sample economic events
  const events = [
    {
      date: new Date().toISOString().split('T')[0],
      time: '10:00',
      event: 'TCMB Faiz Kararı',
      importance: 'HIGH',
      previous: '50%',
      forecast: '50%',
      actual: '-'
    },
    {
      date: new Date().toISOString().split('T')[0],
      time: '14:30',
      event: 'Tüfe Verisi',
      importance: 'HIGH',
      previous: '64.77%',
      forecast: '65.00%',
      actual: '-'
    },
    {
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      time: '11:00',
      event: 'Sanayi Üretimi',
      importance: 'MEDIUM',
      previous: '2.3%',
      forecast: '2.5%',
      actual: '-'
    },
    {
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      time: '16:00',
      event: 'İşsizlik Oranları',
      importance: 'MEDIUM',
      previous: '9.2%',
      forecast: '9.0%',
      actual: '-'
    },
    {
      date: new Date(Date.now() + 172800000).toISOString().split('T')[0],
      time: '09:00',
      event: 'Tüketici Güveni',
      importance: 'LOW',
      previous: '78.5',
      forecast: '79.0',
      actual: '-'
    }
  ];

  const getImportanceColor = (importance) => {
    const colors = {
      'HIGH': 'bg-red-900 text-red-300',
      'MEDIUM': 'bg-yellow-900 text-yellow-300',
      'LOW': 'bg-green-900 text-green-300'
    };
    return colors[importance] || 'bg-gray-900 text-gray-300';
  };

  const getImportanceLabel = (importance) => {
    const labels = {
      'HIGH': 'Yüksek',
      'MEDIUM': 'Orta',
      'LOW': 'Düşük'
    };
    return labels[importance] || importance;
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold">📅 Ekonomik Takvim</h1>
          </div>
          <p className="text-gray-400 text-sm sm:text-base ml-9">Önemli ekonomik göstergeler ve etkinlikler</p>
        </div>

        {/* Legend */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <h3 className="font-bold mb-3 text-sm sm:text-base">Önem Seviyeleri</h3>
          <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-600 rounded"></div>
              <span>Yüksek Etki</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-600 rounded"></div>
              <span>Orta Etki</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-600 rounded"></div>
              <span>Düşük Etki</span>
            </div>
          </div>
        </div>

        {/* Events by Date */}
        <div className="space-y-6">
          {Object.keys(groupedEvents).sort().map((date) => (
            <div key={date}>
              <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-t-lg px-4 py-3">
                <h2 className="text-lg sm:text-xl font-bold">
                  {new Date(date).toLocaleDateString('tr-TR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h2>
              </div>
              <div className="bg-gray-800 rounded-b-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-750">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Saat</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Önem</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Etkinlik</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase hidden md:table-cell">Önceki</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase hidden md:table-cell">Tahmin</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase hidden md:table-cell">Gerçekleşen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {groupedEvents[date].map((event, idx) => (
                        <tr key={idx} className="hover:bg-gray-750">
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm whitespace-nowrap font-medium">{event.time}</td>
                          <td className="px-3 sm:px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getImportanceColor(event.importance)}`}>
                              {getImportanceLabel(event.importance)}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm font-medium">{event.event}</td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-400 hidden md:table-cell">{event.previous}</td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-400 hidden md:table-cell">{event.forecast}</td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm font-bold hidden md:table-cell">{event.actual}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Navigation - Dashboard Style */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 z-50">
        {/* Haberler Dropdown Menu */}
        {showNewsMenu && (
          <div className="absolute bottom-full left-0 right-0 mb-2 px-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-xs mx-auto">
              <button
                onClick={() => {
                  navigate('/news');
                  setShowNewsMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition-colors border-b border-gray-700"
              >
                <Newspaper size={20} className="text-blue-400" />
                <span className="text-white text-sm">Haberler</span>
              </button>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-5 gap-1 py-2">
            {/* İşlem Yap */}
            <button
              onClick={() => navigate('/trading')}
              className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <LineChart size={24} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-400">İşlem Yap</span>
            </button>

            {/* Pozisyonlar */}
            <button
              onClick={() => navigate('/positions')}
              className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Briefcase size={24} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-400">Pozisyonlar</span>
            </button>

            {/* Profil */}
            <button
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <User size={24} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-400">Profil</span>
            </button>

            {/* Haberler */}
            <button
              onClick={() => setShowNewsMenu(!showNewsMenu)}
              className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors relative"
            >
              <Newspaper size={24} className="text-blue-500 mb-1" />
              <span className="text-xs text-blue-400">Haberler</span>
            </button>

            {/* Canlı Destek */}
            <button
              onClick={() => navigate('/support')}
              className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <MessageCircle size={24} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-400">Canlı Destek</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarPage;
